import React, { FC, FormHTMLAttributes } from 'react';
import { Field, Form } from 'react-final-form';
import classNames from 'classnames';

import { t } from 'utils';
import {
  TextInputWrapper,
  SwitchInputWrapper,
  SelectWithEventsFieldsWrapper,
} from 'components/forms';
import { Button, Note } from 'components/ui';
import { IAPIError } from 'utils/apiErrorTransform';
import { Accordion, Error, SuccessMessage, TimifyLoader } from 'components';

import styles from './WebhookForm.module.scss';

export interface IWebhookForm extends FormHTMLAttributes<HTMLFormElement> {
  id: string;
  name: string;
  url: string;
  secret: string;
  secretHeader: string;
  events: any[];
  enableSourceEvents: boolean;
  isActive: boolean;
  hasDeleteButton?: boolean;
  onSubscriptionSubmit: (values: any) => void;
  onRemoveSubscription: (id: string) => void;
}

const WebhookForm: FC<IWebhookForm> = (props) => {
  const {
    id,
    name,
    url,
    secret,
    secretHeader,
    events,
    enableSourceEvents,
    onSubscriptionSubmit,
    onRemoveSubscription,
    isActive,
    className,
    hasDeleteButton,
  } = props;

  const rootClassName = classNames(styles.root, className);

  return (
    <Form
      onSubmit={onSubscriptionSubmit}
      initialValues={{
        id,
        name,
        url,
        secret,
        events,
        isActive,
        secretHeader,
        enableSourceEvents,
        isCustomHeader: secretHeader,
      }}
      validate={(values) => {
        const { isCustomHeader, secretHeader } = values;
        const errors: { [key: string]: string } = {};
        if (isCustomHeader && !secretHeader) errors.secretHeader = t('errors.required');
        const valuesToValidate = ['name', 'secret', 'url', 'events'];
        valuesToValidate.forEach((key: string) => {
          if (!values[key]?.length) errors.key = 'errors.required';
        }, {});
        if (Object.keys(errors).length) return errors;
      }}
      render={({
        values,
        pristine,
        submitting,
        handleSubmit,
        submitErrors,
        submitFailed,
        submitSucceeded,
        hasValidationErrors,
        dirtySinceLastSubmit,
      }) => {
        return (
          <form className={rootClassName} onSubmit={handleSubmit}>
            {submitting && <TimifyLoader overlay />}
            <Accordion
              title={name}
              onRemove={() => onRemoveSubscription(id)}
              className='mb-4'
              hasDeleteButton={hasDeleteButton}
            >
              <Field
                name='name'
                label='webhook.form.name.label'
                placeholder='webhook.form.name.placeholder'
                className='mb-2'
                component={TextInputWrapper}
              />
              <Field
                name='events'
                className='mb-2'
                isRemoveEnabled
                component={SelectWithEventsFieldsWrapper}
                dropdownPlaceholder='app.preview.webhooks.eventsSelect.placeholder'
              />
              <Field
                name='url'
                label='app.preview.webhooks.serverUrl.label'
                placeholder='app.preview.webhooks.serverUrl.placeholder'
                className='mb-1'
                component={TextInputWrapper}
              />
              <Note className='mb-2'>{t('app.preview.webhooks.serverUrl.hintText')}</Note>
              <Field
                name='secret'
                label='app.preview.webhooks.secretKey.label'
                placeholder='app.preview.webhooks.secretKey.placeholder'
                className='mb-1'
                component={TextInputWrapper}
              />
              <Note className='mb-2'>{t('app.preview.webhooks.secretKey.hintText')}</Note>
              <Field
                name='isCustomHeader'
                className={values.isCustomHeader ? 'mb-2' : 'mb-3'}
                label='use.a.custom.header.name'
                type='checkbox'
                component={SwitchInputWrapper}
              />
              <div
                className={classNames('mb-2', {
                  'd-none': !values.isCustomHeader,
                })}
              >
                <Field
                  name='secretHeader'
                  label='app.preview.webhooks.secretHeader.label'
                  placeholder='app.preview.webhooks.secretHeader.placeholder'
                  className='mb-1'
                  component={TextInputWrapper}
                />
                <Note>{t('app.preview.webhooks.secretHeader.hintText')}</Note>
              </div>
              <Field name='id' component='input' type='hidden' />
              <Field
                className='mb-1'
                name='enableSourceEvents'
                label='app.preview.webhooks.activateWebhookFromApp'
                component={SwitchInputWrapper}
                type='checkbox'
              />
              <Note className='mb-2'>
                {t('app.preview.webhooks.activateWebhookFromApp.hintText')}
              </Note>
              <Field
                className='mb-2'
                name='isActive'
                label='app.preview.webhooks.activateWebhook'
                component={SwitchInputWrapper}
                type='checkbox'
              />
              <Button
                variant='primary'
                disabled={
                  (submitFailed && !dirtySinceLastSubmit) ||
                  hasValidationErrors ||
                  pristine ||
                  (submitSucceeded && !dirtySinceLastSubmit)
                }
              >
                {t('save')}
              </Button>
              <SuccessMessage className='mt-2' isSuccess={submitSucceeded}>
                {t('webhook.saved.successfully')}
              </SuccessMessage>
              {submitErrors &&
                Object.values(submitErrors).map((error: IAPIError, i: number) => (
                  <Error
                    key={i}
                    apiError={error}
                    className='mt-2'
                    defaultError='webhook.has.error.on.saving'
                    isError={submitErrors && !submitSucceeded && !dirtySinceLastSubmit}
                  />
                ))}
            </Accordion>
          </form>
        );
      }}
    />
  );
};

export default WebhookForm;
